import React, { useEffect, useMemo, useState, useRef } from 'react';
import Image from 'next/image';
import styles from './index.module.less';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import SVG from 'react-inlinesvg';
import { useRouter } from 'next/router';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { useTranslation } from 'next-i18next';
import { useUserProvider } from '@/context/UserProvider';
import { getInsStoryDashboardData } from '@/lib/service/dashboard';
import { fetchOrderSubsListRequest } from '@/lib/service';

const DashboardContent = dynamic(() => import('../Dashboard'), { ssr: true });
const InstagramDownloadDashboardContent = dynamic(() => import('./InstagramDownloadDashboardContent'), { ssr: false });
const UnFollowDashboardContent = dynamic(() => import('./unFollowDash'), { ssr: false });
const InstagramCommentDashboardContent = dynamic(() => import('./CommentDash'), { ssr: false });
const FollowerExportDashboardContent = dynamic(() => import('./FollowerExport/index'), { ssr: false });

const MainDashboardContent = ({ data }: { data?: any }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { dashboard_type, featureType, record_id } = router.query;
  const [currentType, setCurrentType] = useState(dashboard_type ?? 'track');
  const [currentToolkitType, setCurrentToolkitType] = useState('instagram_download');
  const { isLogin } = useUserProvider();
  const [state, setState] = useState(false);
  const toolkitRef = useRef<HTMLDivElement>(null);

  const loadData = () => {
    fetchOrderSubsListRequest('main', null).then((res) => {
      if (res?.data?.length > 0) {
        setCurrentType('track');
      } else {
        getInsStoryDashboardData().then((res) => {
          if (res?.data?.length > 0) {
            setCurrentType('toolkit');
          } else {
            fetchOrderSubsListRequest('unfollow', null).then((res) => {
              if (res?.data?.length > 0) {
                setCurrentType('toolkit');
                setCurrentToolkitType('unfollower');
              } else {
                setCurrentType('track');
              }
            });
          }
        });
      }
    });
  };

  useEffect(() => {
    if (dashboard_type === undefined && isLogin && !state) {
      setState(true);
      if (router?.asPath?.endsWith('/dashboard')) {
        loadData();
      }
    }
  }, [isLogin]);

  const titleList = useMemo(() => {
    return [
      {
        icon: '/images/landingpage/social_insights_icon.svg',
        title: t('Insights'),
        type: 'track',
      },
      {
        icon: '/images/landingpage/toolkit_icon.svg',
        title: t('Toolkit'),
        type: 'toolkit',
      },
    ];
  }, []);

  const toolkitList = useMemo(() => {
    return [
      {
        icon: '/images/toolkit/toolkit-icon2.png',
        title: t('Instagram Viewer & Downloader'),
        type: 'instagram_download',
      },
      {
        icon: '/images/toolkit/toolkit-icon5.webp',
        title: t('Recently Followed'),
        type: 'recently_followed',
        link: '/instagram-new-follow-tracker',
      },
      {
        icon: '/images/toolkit/toolkit-icon6.webp',
        title: t('Instagram Unfollower Tracker'),
        type: 'unfollower',
      },
      {
        icon: '/images/toolkit/toolkit-icon8.svg',
        title: t('Comment Viewer'),
        type: 'comment',
      },
      {
        icon: '/images/toolkit/toolkit-icon10.svg',
        title: t('Follower Export'),
        type: 'follower_export',
      },
      // {
      //   icon: '/images/toolkit/toolkit-icon4.webp',
      //   title: t('Hashtag'),
      //   type: 'hashtag',
      //   link: '/instagram-hashtag-generator',
      // },
    ];
  }, []);

  useEffect(() => {
    if (featureType) {
      setCurrentToolkitType(featureType as string);
    }
  }, [featureType]);

  useEffect(() => {
    if (featureType && toolkitRef.current) {
      const targetElement = toolkitRef.current.querySelector(`[data-type="${featureType}"]`);
      targetElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: featureType != 'unfollower' ? 'start' : 'center',
      });
    }
  }, [featureType]);

  return (
    <div className={styles.content}>
      <div className={styles.titleContainer}>
        <div className={styles.titleContent}>
          {titleList?.map((item, index) => {
            return (
              <div
                key={item?.type}
                className={classNames([styles.titleItem, currentType === item.type && styles.active])}
                onClick={() => {
                  setCurrentType(item.type);
                  if (item?.type === 'toolkit') {
                    ga_logEvent(EventName.GA_toolkit_button_clicks);
                  }

                  const params = new URLSearchParams({
                    dashboard_type: item.type || '',
                  });
                  window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
                }}
              >
                <SVG
                  src={item.icon}
                  width={'16px'}
                  height={'16px'}
                  title="svg"
                  style={{ color: currentType === item.type ? '#725CFF' : '#000' }}
                />
                <span>{item.title}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: currentType === 'track' ? 'block' : 'none' }}>
        <DashboardContent data={data} />
      </div>

      <div
        style={{
          display: currentType === 'toolkit' || currentType === 'unfollower' ? 'block' : 'none',
        }}
      >
        <div className={styles.titleContainer} style={{ background: '#ECEEF6', padding: '1px 0' }}>
          <div className={styles.titleContent} ref={toolkitRef}>
            {toolkitList?.map((item, index) => {
              return (
                <div
                  key={item?.type}
                  data-type={item.type}
                  className={classNames([
                    styles.titleItem,
                    currentToolkitType === item.type && styles.active,
                    currentToolkitType === item.type && styles.toolkitActive,
                  ])}
                  onClick={() => {
                    if (item?.link) {
                      router.push(item?.link);
                      return;
                    }
                    router.push('/dashboard?dashboard_type=toolkit&featureType=' + item?.type);
                    setCurrentToolkitType(item?.type);
                  }}
                >
                  <Image src={item.icon} alt={''} width={16} height={16} />
                  <span>{item.title}</span>
                </div>
              );
            })}
          </div>
        </div>

        {currentToolkitType === 'instagram_download' && <InstagramDownloadDashboardContent />}
        {currentToolkitType === 'unfollower' && (
          <UnFollowDashboardContent currentToolkitType={currentToolkitType} record_id={record_id} />
        )}
        {currentToolkitType === 'comment' && <InstagramCommentDashboardContent />}
        {currentToolkitType === 'follower_export' && (
          <FollowerExportDashboardContent currentToolkitType={currentToolkitType} />
        )}
      </div>
    </div>
  );
};

export default MainDashboardContent;
